interface PricingSectionProps {
  onSelectFormula: (formula: string) => void;
}

const formulas = [
  {
    name: 'Formule Essentielle',
    price: '19,99',
    icon: 'ri-leaf-line',
    color: 'teal',
    features: [
      '1 visite annuelle d\'entretien préventif',
      'Nettoyage filtres & échangeurs',
      'Contrôles sécurité (pressions, étanchéité, électricité)',
      'Rapport d\'intervention détaillé',
    ]
  },
  {
    name: 'Formule Confort',
    price: '25,99',
    icon: 'ri-star-line',
    color: 'orange',
    popular: true,
    features: [
      '1 visite annuelle (nettoyage complet + réglages)',
      'Optimisation performance & consommation',
      'Petits réglages inclus (hors pièces)',
      'Priorité d\'intervention sous 72h',
    ]
  },
  {
    name: 'Formule Premium Sérénité',
    price: '39,99',
    icon: 'ri-shield-star-line',
    color: 'emerald',
    features: [
      '1 visite annuelle + diagnostic connectivité',
      'Main-d\'œuvre incluse en dépannage (hors pièces)',
      'Déplacement prioritaire sous 24h',
      'Hotline dédiée + 10–15 % sur pièces',
    ]
  }
];

const colorConfig: Record<string, { ring: string; iconBg: string; iconText: string; btn: string; badge: string; badgeText: string }> = {
  teal:    { ring: 'ring-teal-200',    iconBg: 'bg-teal-100',    iconText: 'text-teal-600',   btn: 'bg-teal-500 hover:bg-teal-600',      badge: 'bg-teal-100',    badgeText: 'text-teal-700' },
  orange:  { ring: 'ring-orange-400',  iconBg: 'bg-orange-100',  iconText: 'text-orange-600', btn: 'bg-orange-500 hover:bg-orange-600',  badge: 'bg-orange-500',  badgeText: 'text-white' },
  emerald: { ring: 'ring-emerald-200', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600',btn: 'bg-emerald-600 hover:bg-emerald-700',badge: 'bg-emerald-100', badgeText: 'text-emerald-700' },
};

const PricingSection = ({ onSelectFormula }: PricingSectionProps) => {
  return (
    <section id="formules" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="text-center mb-14">
          <span className="inline-block bg-orange-100 text-orange-600 text-sm font-bold px-4 py-1.5 rounded-full mb-4">Nos formules</span>
          <h2 className="text-4xl font-extrabold text-slate-900 mb-4">
            Choisissez votre formule d'entretien
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Des formules adaptées à tous les besoins pour protéger et optimiser votre pompe à chaleur.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-7">
          {formulas.map((formula, i) => {
            const c = colorConfig[formula.color];
            return (
              <div
                key={i}
                className={`relative bg-white rounded-3xl p-8 ring-2 ${c.ring} flex flex-col ${formula.popular ? 'scale-105 shadow-xl' : ''}`}
              >
                {formula.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className={`${c.badge} ${c.badgeText} text-xs font-extrabold px-4 py-1.5 rounded-full shadow`}>
                      ⭐ Plus populaire
                    </span>
                  </div>
                )}

                <div className={`w-14 h-14 ${c.iconBg} rounded-2xl flex items-center justify-center mb-5`}>
                  <i className={`${formula.icon} ${c.iconText} text-2xl`}></i>
                </div>

                <h3 className="text-xl font-extrabold text-slate-900 mb-2">{formula.name}</h3>
                <div className="flex items-end gap-1 mb-6">
                  <span className="text-4xl font-black text-slate-900">{formula.price} €</span>
                  <span className="text-slate-400 text-sm mb-1">/ mois</span>
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {formula.features.map((feat, j) => (
                    <li key={j} className="flex items-start gap-3 text-sm text-slate-700">
                      <div className={`w-5 h-5 ${c.iconBg} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <i className={`ri-check-line ${c.iconText} text-xs`}></i>
                      </div>
                      {feat}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => onSelectFormula(formula.name)}
                  className={`w-full ${c.btn} text-white font-bold py-3.5 px-6 rounded-xl transition-colors cursor-pointer whitespace-nowrap`}
                >
                  Sélectionner cette formule
                </button>
              </div>
            );
          })}
        </div>

        {/* Info strip */}
        <div className="mt-12 grid sm:grid-cols-3 gap-5">
          {[
            { icon: 'ri-shield-check-line', title: 'Sans engagement', desc: 'Résiliez à tout moment, sans frais ni pénalité.' },
            { icon: 'ri-tools-line', title: 'Techniciens certifiés', desc: 'Tous nos intervenants sont certifiés RGE et formés constructeur.' },
            { icon: 'ri-customer-service-line', title: 'Hotline dédiée', desc: 'Une ligne directe pour nos clients sous contrat, 7j/7.' },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 flex items-start gap-4">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-orange-500 text-lg`}></i>
              </div>
              <div>
                <p className="font-bold text-slate-800 mb-1">{item.title}</p>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
